/**
 * Swamithra — the wire naming convention, applied in one place.
 *
 * THE SERVICE SPEAKS snake_case. The Go API decodes request bodies with
 * `DisallowUnknownFields`, so `{"mobileNumber": "+91..."}` on `POST /auth/lookup`
 * is not "a field it ignores" — it is a 400 `invalid_body`, and the entry screen
 * never gets past the first keystroke. Its responses are snake_case for the same
 * reason: they are Go structs with snake_case JSON tags.
 *
 * THIS PACKAGE SPEAKS camelCase, because it is TypeScript and because every
 * schema, hook and component in the three apps already reads `submittedAt`.
 *
 * Rather than spell the conversion into forty Zod schemas — where the next field
 * added is the one somebody forgets — it happens once, here, at the transport
 * boundary in ./client:
 *
 *   outbound  bodies and query parameters   camelCase → snake_case
 *   inbound   the parsed JSON, BEFORE Zod   snake_case → camelCase
 *
 * BOTH DIRECTIONS ARE IDEMPOTENT. A key with no underscore is left alone on the
 * way in and a key with no capital is left alone on the way out, so a payload
 * that is already in the target convention passes through untouched. That is
 * what lets a test — or a service that changes its mind — hand this layer either
 * spelling without a special case.
 *
 * WHAT IS NOT CONVERTED: values. Only keys are rewritten, so an enum
 * (`in_progress`), a signed URL's query string and a rich-text body survive
 * exactly as sent. The one place that bites is an object whose KEYS are enum
 * values — the reporting status split — and the schema that reads it accepts
 * both spellings for precisely that reason (see ../domain/reporting).
 */

/** `mobileNumber` → `mobile_number`. Already-snake keys are returned unchanged. */
export function toWireKey(key: string): string {
	return key
		.replace(/([a-z])([A-Z])/g, "$1_$2")
		.replace(/([A-Za-z])([0-9])/g, "$1_$2")
		.replace(/([0-9])([A-Za-z])/g, "$1_$2")
		.toLowerCase()
}

/** `mobile_number` → `mobileNumber`. Already-camel keys are returned unchanged. */
export function fromWireKey(key: string): string {
	return key.replace(/_+([a-z0-9])/g, (_match, char: string) => char.toUpperCase())
}

/**
 * True for something whose own keys are ours to rename.
 *
 * A Blob, a FormData, a Date and a URL are values, not envelopes: recursing into
 * one would produce a plain object with the same fields and none of the
 * behaviour, which is a bug that only shows up at the point of upload.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
	if (value === null || typeof value !== "object") return false
	if (Array.isArray(value)) return false

	const proto: unknown = Object.getPrototypeOf(value) as unknown
	if (proto === null) return true

	// REALM-TOLERANT, and it has to be. `proto === Object.prototype` is an
	// identity check against THIS realm's Object, and a JSON body parsed by a
	// fetch implementation that lives in another one — Node's under the Expo
	// app's Jest environment, a worker's, a vm context's — has a different
	// `Object.prototype`. The check then fails for every response body and the
	// conversion silently does nothing: `mobile_number` never becomes
	// `mobileNumber`, every schema reads `undefined`, and nothing errors.
	//
	// Walking one step up says the same thing without naming a realm: a plain
	// object's prototype is the object whose own prototype is null. A Date, a
	// Blob, a FormData, a URL and any class instance all sit one level deeper,
	// so they are still treated as values rather than envelopes.
	return Object.getPrototypeOf(proto) === null
}

function convert(value: unknown, key: (name: string) => string): unknown {
	if (Array.isArray(value)) return value.map(item => convert(item, key))
	if (!isPlainObject(value)) return value

	const out: Record<string, unknown> = {}
	for (const [name, item] of Object.entries(value)) {
		out[key(name)] = convert(item, key)
	}
	return out
}

/** A request body, in the convention the service decodes. */
export function toWire(value: unknown): unknown {
	return convert(value, toWireKey)
}

/** A response body, in the convention this package's schemas read. */
export function fromWire(value: unknown): unknown {
	return convert(value, fromWireKey)
}

/**
 * Query-string parameters, in the service's convention.
 *
 * Separate from `toWire` because a query string is flat and because a parameter
 * whose name the service fixed — `page_size`, `from`, `to` — is passed through
 * already spelled that way and must survive the trip.
 */
export function toWireQuery(
	query: Record<string, string | number | boolean | undefined | null>,
): Record<string, string | number | boolean | undefined | null> {
	const out: Record<string, string | number | boolean | undefined | null> = {}
	for (const [name, value] of Object.entries(query)) {
		out[toWireKey(name)] = value
	}
	return out
}
